import React, { useState, useEffect } from "react";
import { Warehouse } from "../../types/Warehouse";
import Button from "../ui/Button";
import { useFetchContext } from "../../context/FetchContext";
import { useToast } from "../../context/ToastContext";
import { IDataPagination } from "types/User";
import Select from "components/ui/Select";
import moment from "moment";
import axios from "axios";

interface warehouseLocation {
  name: string;
}
interface Product {
  id: string;
  uuid: string;
  productCode: string;
  serialNumber: string;
  quantity: number;
  royalty: number;
  warehouseLocationId: number;
  warehouseLocation: warehouseLocation;
}

interface Royalty {
  id: string;
  uuid: string;
  ddtNumber: string;
  externalId: string;
  createdAt: string;
  date: string;
  orderDateTime: string;
  shippingDateTime: string;
  customerDisplayName: string;
  destinationDisplayName: string;
  carriageDescription: string;
  shipmentSupplierCost: number;
  shipmentCost: number;
  province: string;
  products: Product[];
  warehouseId: number;
  status: string;
  location?: string;
}

const RoyaltyList: React.FC = () => {
  const [editingRoyalty, setEditingRoyalty] = useState<{
    type: "product" | "shipment";
    uuid: string;
    value: number;
  } | null>(null);

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [royalties, setRoyalties] = useState<Royalty[]>([]);
  const [customerOrderDates, setCustomerOrderDates] = useState<
    Map<string, string[]>
  >(new Map());
  const {
    fetchData,
    state: { isLoading },
  } = useFetchContext();
  const { showToast } = useToast();

  const currentMonth = new Date().getMonth() + 1;
  const allMonths = [
    { value: "1", label: "Gennaio" },
    { value: "2", label: "Febbraio" },
    { value: "3", label: "Marzo" },
    { value: "4", label: "Aprile" },
    { value: "5", label: "Maggio" },
    { value: "6", label: "Giugno" },
    { value: "7", label: "Luglio" },
    { value: "8", label: "Agosto" },
    { value: "9", label: "Settembre" },
    { value: "10", label: "Ottobre" },
    { value: "11", label: "Novembre" },
    { value: "12", label: "Dicembre" },
  ];
  const months =
    selectedYear === currentYear
      ? allMonths.filter((month) => parseInt(month.value) <= currentMonth)
      : allMonths;

  // Fetch warehouses from API
  useEffect(() => {
    const getWarehouses = async () => {
      try {
        const response = await fetchData<IDataPagination<Warehouse>>(
          "/warehouses"
        );
        if (response && response.data && response.data.items) {
          setWarehouses(response.data.items);
        }
      } catch (error) {
        console.error("Error fetching warehouses:", error);
        showToast("Errore durante il caricamento dei magazzini", "error");
      }
    };

    getWarehouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedWarehouse && selectedMonth) {
      fetchRoyalties();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarehouse, selectedMonth, selectedYear]);

  // Identificazione degli ordini multipli dello stesso cliente in un giorno
  useEffect(() => {
    if (royalties.length > 0) {
      const orderDatesByCustomerAndDestination = new Map<string, string[]>();

      royalties.forEach((royalty) => {
        const customer = royalty.customerDisplayName;
        const destination = royalty.destinationDisplayName;
        // Consideriamo solo la data, ignorando l'orario
        const orderDate = moment(royalty.createdAt).format("YYYY-MM-DD");
        const key = `${customer}-${destination}-${orderDate}`;

        if (!orderDatesByCustomerAndDestination.has(key)) {
          orderDatesByCustomerAndDestination.set(key, []);
        }

        const orders = orderDatesByCustomerAndDestination.get(key);
        if (orders && !orders.includes(royalty.id)) {
          orders.push(royalty.id);
        }
      });

      setCustomerOrderDates(orderDatesByCustomerAndDestination);
    }
  }, [royalties]);

  const fetchRoyalties = async () => {
    try {
      const response = await fetchData<Royalty[]>(
        `/documents/royalties?warehouseId=${selectedWarehouse}&month=${selectedMonth}&year=${selectedYear}`
      );
      if (response && response.data) {
        // Ordina per data (solo giorno, ignorando l'orario) e cliente
        const sortedRoyalties = [...response.data].sort((a, b) => {
          // Prima ordina per data (solo giorno)
          const dateA = moment(a.createdAt).startOf("day");
          const dateB = moment(b.createdAt).startOf("day");
          const dateCompare = dateA.valueOf() - dateB.valueOf();

          if (dateCompare !== 0) return dateCompare;

          // Se la data è uguale, ordina per cliente
          return a.customerDisplayName.localeCompare(b.customerDisplayName);
        });

        setRoyalties(sortedRoyalties);
      }
    } catch (error) {
      console.error("Error fetching royalties:", error);
      showToast("Errore durante il caricamento delle royalty", "error");
    }
  };

  const handleWarehouseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedWarehouse(e.target.value);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMonth(e.target.value);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(Number(e.target.value));
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await axios({
        url: `/documents/royalties/export?warehouseId=${selectedWarehouse}&month=${selectedMonth}&year=${selectedYear}`,
        method: "GET",
        responseType: "blob",
      });

      if (response && response.data) {
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        const dateTimeString = moment().format("DD-MM-YYYY-10-59");

        link.setAttribute(
          "download",
          `royalties_${selectedYear}_${selectedMonth}_${dateTimeString}.xlsx`
        );

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode?.removeChild(link);
      }
    } catch (error) {
      console.error("Error downloading excel:", error);
      showToast("Errore durante il download del file Excel", "error");
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  // Verifica se un cliente ha effettuato più ordini nella stessa giornata
  const hasMultipleOrders = (royalty: Royalty) => {
    const customer = royalty.customerDisplayName;
    const destination = royalty.destinationDisplayName;
    // Consideriamo solo la data, ignorando l'orario
    const orderDate = moment(royalty.createdAt).format("YYYY-MM-DD");
    const key = `${customer}-${destination}-${orderDate}`;
    const orders = customerOrderDates.get(key);
    return orders && orders.length > 1;
  };
  const handleEditClick = (
    type: "product" | "shipment",
    uuid: string,
    value: number
  ) => {
    setEditingRoyalty({ type, uuid, value });
  };

  const handleSaveEdit = async () => {
    if (!editingRoyalty) return;

    try {
      if (editingRoyalty.type === "product") {
        await fetchData(`/products/${editingRoyalty.uuid}`, "PUT", {
          royalty: editingRoyalty.value,
        });
      } else {
        await fetchData(`/documents/${editingRoyalty.uuid}`, "PUT", {
          shipmentSupplierCost: editingRoyalty.value,
        });
      }

      // Refresh data
      fetchRoyalties();
      setEditingRoyalty(null);
      showToast("Modifiche salvate con successo", "success");
    } catch (error) {
      console.error("Error updating value:", error);
      showToast("Errore durante il salvataggio", "error");
    }
  };

  const handleCancelEdit = () => {
    setEditingRoyalty(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editingRoyalty) return;

    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setEditingRoyalty({ ...editingRoyalty, value });
    }
  };

  // Prepara i dati da visualizzare nella tabella
  const prepareTableData = () => {
    const tableRows: JSX.Element[] = [];

    royalties.forEach((royalty) => {
      const isMultiOrder = hasMultipleOrders(royalty);
      const products = royalty.products;
      const hasMultipleProducts = products.length > 1;

      const rowsForRoyalty = products.map((product, productIndex) => {
        const isFirstProduct = productIndex === 0;
        const isLastProduct = productIndex === products.length - 1;

        let rowClasses = isMultiOrder ? "bg-yellow-50" : "";

        const rowStyle: React.CSSProperties = {};

        if (hasMultipleProducts) {
          if (isFirstProduct) {
            rowStyle.borderTop = "1px solid #6b7280";
          }
          if (isLastProduct) {
            rowStyle.borderBottom = "1px solid #6b7280";
          }
        }

        return (
          <tr
            key={`${royalty.id}-${product.id}`}
            className={rowClasses}
            style={rowStyle}
          >
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm font-medium text-gray-900">
                {product.productCode}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-500">
                {product.serialNumber}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-500">{royalty.ddtNumber}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-500">
                {moment(royalty.createdAt).format("DD/MM/YYYY HH:mm")}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-500">
                {moment(royalty.date).format("DD/MM/YYYY HH:mm")}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-500">
                {royalty.customerDisplayName}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-500">
                {royalty.destinationDisplayName}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-500 flex items-center">
                {editingRoyalty?.type === "product" &&
                editingRoyalty?.uuid === product.uuid ? (
                  <>
                    <input
                      type="number"
                      value={editingRoyalty.value}
                      onChange={handleInputChange}
                      className="w-24 px-2 py-1 border rounded"
                      step="0.01"
                    />
                    <button
                      onClick={handleSaveEdit}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="ml-1 text-red-600 hover:text-red-800"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </>
                ) : (
                  <>
                    {formatCurrency(product.royalty || 0)}
                    <button
                      onClick={() =>
                        handleEditClick(
                          "product",
                          product.uuid,
                          product.royalty || 0
                        )
                      }
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                  </>
                )}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-500 flex items-center">
                {isLastProduct ? (
                  editingRoyalty?.type === "shipment" &&
                  editingRoyalty?.uuid === royalty.uuid ? (
                    <>
                      <input
                        type="number"
                        value={editingRoyalty.value}
                        onChange={handleInputChange}
                        className="w-24 px-2 py-1 border rounded"
                        step="0.01"
                      />
                      <button
                        onClick={handleSaveEdit}
                        className="ml-2 text-green-600 hover:text-green-800"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="ml-1 text-red-600 hover:text-red-800"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </>
                  ) : (
                    <>
                      {formatCurrency(royalty.shipmentSupplierCost)}
                      <button
                        onClick={() =>
                          handleEditClick(
                            "shipment",
                            royalty.uuid,
                            royalty.shipmentSupplierCost
                          )
                        }
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                    </>
                  )
                ) : (
                  ""
                )}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-500">{royalty.province}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-500">
                {royalty.carriageDescription}
              </div>
            </td>
          </tr>
        );
      });

      tableRows.push(...rowsForRoyalty);
    });

    return tableRows;
  };
  return (
    <div className="overflow-hidden bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h2 className="text-lg font-medium text-gray-900">Gestione Royalty</h2>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <label
              htmlFor="warehouse"
              className="block text-sm font-medium text-gray-700"
            >
              Seleziona Magazzino
            </label>
            <Select
              id="warehouse"
              value={selectedWarehouse}
              onChange={handleWarehouseChange}
              className="mt-1"
              fullWidth
            >
              <option value="">Seleziona un magazzino</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label
              htmlFor="year"
              className="block text-sm font-medium text-gray-700"
            >
              Seleziona Anno
            </label>
            <Select
              id="year"
              value={selectedYear.toString()}
              onChange={handleYearChange}
              className="mt-1"
              fullWidth
            >
              <option value={currentYear.toString()}>{currentYear}</option>
              <option value={(currentYear - 1).toString()}>
                {currentYear - 1}
              </option>
            </Select>
          </div>

          <div>
            <label
              htmlFor="month"
              className="block text-sm font-medium text-gray-700"
            >
              Seleziona Mese
            </label>
            <Select
              id="month"
              value={selectedMonth}
              onChange={handleMonthChange}
              className="mt-1"
              fullWidth
            >
              <option value="">Seleziona un mese</option>
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex justify-end py-2">
            <Button
              onClick={handleDownloadExcel}
              variant="secondary"
              disabled={!selectedWarehouse || !selectedMonth}
              className="mb-0"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </Button>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200">
        {isLoading ? (
          <div className="text-center py-4">
            <svg
              className="animate-spin h-8 w-8 mx-auto text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="mt-2 text-gray-500">Caricamento dati...</p>
          </div>
        ) : !selectedWarehouse || !selectedMonth ? (
          <div className="text-center py-4">
            <p className="text-gray-500">
              Seleziona un magazzino e un mese per visualizzare i dati
            </p>
          </div>
        ) : royalties.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-gray-500">Nessun dato trovato</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Prodotto
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Matricola
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    DDT
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Data Ora Ordine
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Data Ora Evasione
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Cliente
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Destinazione
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Compenso Prodotto
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Costo Trasporto
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Provincia
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Descrizione Spedizione
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y">{prepareTableData()}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoyaltyList;
